"use client"

import React, { useState } from 'react'
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { HelpCircle, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"

interface FAQItem {
  question: string
  answer: string
}

interface BlogFAQSectionProps {
  faqs: FAQItem[]
  className?: string
  hideHeaderOnMobile?: boolean
}

export function BlogFAQSection({
  faqs,
  className,
  hideHeaderOnMobile = false
}: BlogFAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null) // All closed by default

  if (!faqs || faqs.length === 0) {
    return null
  }

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  // FAQ items content (shared between card and no-card versions)
  const faqContent = (
    <div className="space-y-3">
      {faqs.map((faq, index) => {
        const isOpen = openIndex === index

        return (
          <div
            key={index}
            className={cn(
              "bg-white rounded-xl border-2 transition-all duration-300 ease-out overflow-hidden",
              isOpen
                ? "border-green-400 shadow-lg shadow-green-100/50"
                : "border-gray-200 hover:border-green-300 hover:shadow-md"
            )}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full text-left px-4 py-4 md:px-5 md:py-4 flex items-center justify-between gap-3 hover:bg-green-50/50 transition-all duration-200 group"
              aria-expanded={isOpen}
            >
              <h3 className={cn(
                "text-sm md:text-base font-semibold leading-snug transition-colors duration-200 text-left",
                isOpen ? "text-green-700" : "text-gray-900 group-hover:text-green-700"
              )}>
                {faq.question}
              </h3>
              <ChevronDown className={cn(
                "h-5 w-5 flex-shrink-0 transition-all duration-300",
                isOpen ? "text-green-600 rotate-180" : "text-gray-400 group-hover:text-green-600"
              )} />
            </button>
            <div
              className={cn(
                "overflow-hidden transition-all duration-300 ease-out",
                isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
              )}
            >
              <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                <div className="pl-4 border-l-4 border-green-300 bg-green-50/30 rounded-r-lg py-3 px-4">
                  <p
                    data-faq-answer="true"
                    className="text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-line"
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  // Render with full Card wrapper
  return (
    <Card className={cn("bg-white border border-gray-200 shadow-xl overflow-hidden", className)}>
      <CardHeader className={cn(
        "pb-5 pt-8 px-6 md:px-8 bg-linear-to-r from-green-50 via-emerald-50 to-green-50 border-b border-green-100",
        hideHeaderOnMobile && "hidden md:block"
      )}>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-green-100 rounded-xl shadow-xs flex-shrink-0">
            <HelpCircle className="h-6 w-6 md:h-7 md:w-7 text-green-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold leading-none tracking-tight text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-2">
              Common questions about this topic
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6 md:p-8">
        {faqContent}
      </CardContent>
    </Card>
  )
}

